import { useEffect } from 'react';
import { useWorldOptions } from '../api/queries';
import { GLOBAL_WORLD, WORLD_STATUS_LABELS } from '../api/types';

/**
 * Weltauswahl für Anmeldung und Registrierung. Bei der Anmeldung steht
 * zusätzlich der weltübergreifende Zugang für Administratoren zur Wahl.
 */
export function WorldSelect({
  purpose,
  value,
  onChange,
  error,
  disabled,
}: {
  purpose: 'login' | 'register';
  value: string;
  onChange: (value: string) => void;
  error?: string | undefined;
  disabled?: boolean;
}) {
  const worlds = useWorldOptions(purpose);
  const options = worlds.data?.worlds ?? [];

  // Eine gemerkte Auswahl kann hier ungültig sein -- etwa der
  // weltübergreifende Zugang im Registrierungsformular oder eine inzwischen
  // archivierte Welt. Sonst zeigte das Feld etwas anderes an als der Zustand.
  const isValid =
    value === '' ||
    (purpose === 'login' && value === GLOBAL_WORLD) ||
    options.some((world) => world.key === value);

  useEffect(() => {
    if (!worlds.isPending && !isValid) {
      onChange('');
    }
  }, [worlds.isPending, isValid, onChange]);

  return (
    <label className="field">
      <span>Welt</span>
      <select
        name="worldKey"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        disabled={disabled || worlds.isPending}
        required
      >
        <option value="" disabled>
          {worlds.isPending ? 'Welten werden geladen …' : 'Welt wählen …'}
        </option>

        {purpose === 'login' && (
          <option value={GLOBAL_WORLD}>Administration (weltübergreifend)</option>
        )}

        {options.map((world) => (
          <option key={world.key} value={world.key}>
            {world.name}
            {world.status === 'closed' ? ' — Registrierung geschlossen' : ''}
          </option>
        ))}
      </select>

      {!worlds.isPending && options.length === 0 && (
        <small className="hint">
          {purpose === 'register'
            ? 'Zurzeit ist keine Welt für Registrierungen geöffnet. Wende dich an einen Administrator.'
            : 'Es ist noch keine Welt angelegt.'}
        </small>
      )}

      {worlds.isError && (
        <small className="field-error">Die Weltliste konnte nicht geladen werden.</small>
      )}

      {error && <small className="field-error">{error}</small>}

      {purpose === 'register' && value && value !== GLOBAL_WORLD && (
        <small className="hint">
          Der Account gilt nur für diese Welt
          {options.find((world) => world.key === value)?.status === 'closed'
            ? ` (${WORLD_STATUS_LABELS.closed})`
            : ''}
          .
        </small>
      )}
    </label>
  );
}
